import type { InterfacePermissions } from '@unifyapps/defs/types/interface';
import { useMemo } from 'react';
import { EMPTY_OBJECT } from '../../../consts/empty';
import { isEmptyObject } from '../../../utils/object';
import { useGetGlobalAppStateStoreState } from '../../stores/app/GlobalAppStateStore';
import { useGetInterfaceStoreState } from '../../stores/InterfaceStore';
import { hasAccess } from '../utils/hasAccess';

// Both `mode` and `permissions` are set once when the GlobalAppStateStore is
// initialised (see `BaseGlobalAppStateStoreProvider.useState(() => ...)`); no
// in-store setter exists for either, and runtime changes are propagated by the
// `<Remount>` wrapper which creates a new store entirely (subtree remount). So
// reading both non-reactively is safe — and avoids registering one Zustand
// subscriber per rendered block. If `permissions` ever becomes mutable in-place,
// switch `getGlobalAppStateStoreState().permissions` to a reactive selector and
// keep `mode` non-reactive.

export const useCanAccess = (permissionsToAccess: InterfacePermissions = EMPTY_OBJECT) => {
  const { getGlobalAppStateStoreState } = useGetGlobalAppStateStoreState();
  const { getInterfaceStoreState } = useGetInterfaceStoreState();

  return useMemo(() => {
    if (isEmptyObject(permissionsToAccess)) {
      return true;
    }
    return hasAccess(
      getGlobalAppStateStoreState().permissions,
      permissionsToAccess,
      getInterfaceStoreState().mode,
    );
  }, [getGlobalAppStateStoreState, getInterfaceStoreState, permissionsToAccess]);
};
